import type { RoleType } from './enums';
import type { AppMeta } from './app-meta.types';

// Matches scio2 Domains model — branding/white-label configuration.
// Stored on both MeResponse.brandingData and CompanyProfile.brandingData.
export interface BrandingData {
  _id?: string;
  email: string;
  isVerifiedEmail: boolean;
  emailFullname: string;
  accountId?: string;
  createdBy?: string;
  /** Custom domain (e.g. "branded.example.com") — takes precedence over subHost. */
  host?: string;
  /** Subdomain prefix used with the OptiSigns domain — resolves to `${subHost}.${brandingZone}`. */
  subHost?: string;
  /**
   * The account's portal ORIGIN, resolved BY THE BACKEND — `https://{host}` or
   * `https://{subHost}.{brandingZone}`, falling back to the default portal. No trailing slash.
   *
   * **Always prefer this over assembling a host yourself.** The branding zone is per-environment
   * (prod `optisigns.net`, QA `opstech.org`) and the frontend cannot know it. Hardcoding
   * `.optisigns.net` made the SAML Settings page show — and persist into `samlCfgs.audienceURI` — a
   * prod-zone URL on QA, which the IdP then rejected (#1914). open-api resolves this with the same
   * `getPortalOrigin()` helper it uses for the SAML AuthnRequest, so the two agree by construction.
   */
  portalOrigin?: string;
  /** The environment's branding zone (prod `optisigns.net`, QA `opstech.org`) — for `{subHost}.{zone}` labels. */
  brandingZone?: string;
  colorTheme: string;
  emailFrom: string;
  hideOptiSignsBrandedApps: boolean;
  hidePaymentHistoryPages: boolean;
  hideSubscriptionPages: boolean;
  removeOptiSignsBranding: boolean;
  hideCreateAccountButton: boolean;
  hideAccountMembersPage: boolean;
  hideExternalCommunications: boolean;
  customBrandCanvas: boolean;
  customBrandCanva: boolean;
  branding: boolean;
  isAllowGoogleLoginSocial: boolean;
  isAllowFBLoginSocial: boolean;
  isAllowMSLoginSocial: boolean;
  backgoundLogin: string;
  favicon: string;
  logoCompany: string;
  siteTitle: string;
  logo: string;
  templateCategories: string[];
  isWhiteLabel: boolean;
  isCustomDomain?: boolean;
  isInit?: boolean;
  styles?: string;
  stylesBox?: string;

  // ── Additional Branding Settings page fields (from getMyDomain) ──
  /** Login box position: ngTopCenter | ngTopLeft | ngTopRight | ngMiddleLeft | ngCenter | ngMiddleRight | ngBottonLeft | ngBottonRight | ngBottonCenter (scio1 typo "Botton" preserved). */
  loginBox?: string;
  loginBoxColor?: string;
  loginBoxPadding?: number;
  loginBoxOpacity?: number;
  loginBoxMarginLeftRight?: number;
  loginBoxMarginTopBottom?: number;

  /** scio1 typo preserved: "hideProofofPlay" not "hideProofOfPlay". */
  hideProofofPlay?: boolean;
  hideAuditLogging?: boolean;
  hideMonitorAndAlert?: boolean;

  /** Stored as String in legacy data; new writes are Boolean. See migrate-domain-active-host.ts. */
  isActiveHost?: boolean;
  /** Set when child host saves its own branding so parent updates stop overriding. */
  isNotParentUpdate?: boolean;
  /** True when account inherited branding from another account (host/subhost match). */
  isChildHost?: boolean;

  appManagement?: string[];
  appManagementForShareLinks?: string[];
  presenterUrl?: string;
}

/** Default branding values for non-white-label accounts (matches scio2 AuthService.branding getter) */
export const BRANDING_DEFAULTS: BrandingData = {
  email: 'support@optisign.com',
  isVerifiedEmail: true,
  emailFullname: 'OptiSigns',
  colorTheme: '#1abF7D',
  emailFrom: 'support@optisign.com',
  hideOptiSignsBrandedApps: false,
  hidePaymentHistoryPages: false,
  hideSubscriptionPages: false,
  removeOptiSignsBranding: false,
  hideCreateAccountButton: false,
  hideAccountMembersPage: false,
  hideExternalCommunications: false,
  customBrandCanvas: true,
  customBrandCanva: true,
  branding: true,
  isAllowGoogleLoginSocial: false,
  isAllowFBLoginSocial: false,
  isAllowMSLoginSocial: false,
  backgoundLogin: '',
  favicon: '/images/shortcut-icon.png',
  logoCompany: 'OS',
  siteTitle: 'OptiSigns',
  logo: '',
  templateCategories: [],
  isWhiteLabel: false,
};

/**
 * Resolve branding from raw API `brandingData`, matching scio2 `AuthService.branding` getter.
 *
 * - Non-white-label: returns BRANDING_DEFAULTS
 * - White-label: merges raw data over defaults, prepends S3 bucket URLs to
 *   logo/favicon/backgoundLogin/logoCompany
 *
 * @param raw — `brandingData` from MeResponse or CompanyProfile (may be partial)
 * @param awsBucketName — S3 bucket name (e.g. `VITE_AWS_BUCKET_NAME`)
 */
export function resolveBranding(
  raw: Partial<BrandingData> | undefined | null,
  awsBucketName = 'optisigns-assets',
): BrandingData {
  if (!raw?.isWhiteLabel) {
    // `host`/`subHost` are domain IDENTITY, not white-label PRESENTATION. Preserve them even
    // on the non-white-label build so domain-dependent features work off the store `me`
    // (SAML "set up your domain" gate in SamlSettingsPage, branding-email-incomplete warning
    // in AccountMembersPage). Branding VISIBILITY stays keyed off `isWhiteLabel` (still false
    // here), so no branded UI leaks. Without this, formatBranding() strips host/subHost on
    // app.optisigns.com and the SAML toggle is permanently disabled for every account.
    //
    // `portalOrigin`/`brandingZone` are domain IDENTITY for the same reason — the SAML Settings page
    // needs them to render the SSO/ACS URLs even on the non-white-label build (#1914).
    return {
      ...BRANDING_DEFAULTS,
      host: raw?.host,
      subHost: raw?.subHost,
      portalOrigin: raw?.portalOrigin,
      brandingZone: raw?.brandingZone,
    };
  }

  const s3Base = `https://${awsBucketName}.s3.amazonaws.com/`;
  const resolved: BrandingData = {
    ...BRANDING_DEFAULTS,
    ...raw,
    isWhiteLabel: true,
    isCustomDomain: true,
    isInit: true,
  };

  if (resolved.logo) {
    resolved.logo = s3Base + resolved.logo;
  }
  if (resolved.favicon) {
    resolved.favicon = s3Base + resolved.favicon;
  }
  resolved.backgoundLogin = resolved.backgoundLogin ? s3Base + resolved.backgoundLogin : '';
  // `logoCompany === 'OS'` is the sentinel for "use the default OptiSigns logo" (BRANDING_DEFAULTS),
  // NOT an S3 key — don't prepend the bucket (that yields `<bucket>.s3.amazonaws.com/OS` → broken
  // image). Keep it as 'OS' so the LOGIN page (BrandLogo's `logoCompany !== 'OS'` guard) falls back
  // to the site-title text. Only a real custom-logo key gets the S3 URL. (`logoCompany` is the
  // "Login Page → Logo" field; the header logo is `logo`, the "Branding → Logo" slot.)
  resolved.logoCompany =
    resolved.logoCompany && resolved.logoCompany !== 'OS' ? s3Base + resolved.logoCompany : 'OS';

  return resolved;
}

/** Fallback when the backend hasn't sent `brandingZone` (older API). Prod's zone. */
const DEFAULT_BRANDING_ZONE = 'optisigns.net';

/**
 * The environment's branding zone — prod `optisigns.net`, QA `opstech.org`.
 *
 * Comes from the backend (`BRANDING_ZONE`); the frontend cannot infer it. Use this for any
 * `{subHost}.{zone}` label instead of writing the literal.
 */
export function resolveBrandingZone(
  brandingData: Pick<BrandingData, 'brandingZone'> | undefined | null,
): string {
  return brandingData?.brandingZone || DEFAULT_BRANDING_ZONE;
}

/**
 * Is `host` a VANITY (customer-owned) domain rather than a `{subHost}.{brandingZone}` portal?
 *
 * Mirrors the backend's single source of truth — `optisigns-open-api`
 * `domain.service.ts:getHost()` computes `isCustomHost = !!host && !host.endsWith('.' + brandingZone)`
 * and gates it on the `brandingCustomDomain` ADD-ON, while a subHost only needs `feature.branding`.
 * Keep the two in step: a divergence here means the frontend offers a flow the backend refuses.
 *
 * The zone comes from the environment (prod `optisigns.net`, QA `opstech.org`, local
 * `localhost:5175`) — pass it in via `resolveBrandingZone()`, never a literal (#1914).
 *
 * NOTE this is only the HOST-SHAPE test. `app.optisigns.com` is not in the zone either, so callers
 * must additionally establish that this is a white-label deployment at all (`isWhiteLabelBuild()` +
 * a resolved branding record) before treating a host as a customer's vanity domain.
 */
export function isCustomDomainHost(
  host: string | undefined | null,
  brandingZone: string | undefined | null,
): boolean {
  if (!host) return false;
  const normalizedHost = host.toLowerCase();
  const zone = (brandingZone || DEFAULT_BRANDING_ZONE).toLowerCase();
  if (!zone) return false;
  return normalizedHost !== zone && !normalizedHost.endsWith(`.${zone}`);
}

/**
 * The account's portal ORIGIN — `https://host`, no trailing slash. `''` when the account has no
 * branding domain at all.
 *
 * **Resolved by the backend** (`me.brandingData.portalOrigin`), which open-api derives with the same
 * `getPortalOrigin()` helper it uses for the SAML AuthnRequest's ACS — so what the portal shows an
 * admin to register and what the backend actually sends agree by construction.
 *
 * Never reconstruct this from `subHost` + a literal zone. The zone is per-environment, so hardcoding
 * `.optisigns.net` made QA display — and PERSIST into `samlCfgs.audienceURI` — a prod-zone URL that
 * the IdP rejected with AADSTS700016 (#1914). The `host`/`subHost` path below is only a fallback for
 * a backend that predates `portalOrigin`.
 */
export function resolvePortalOrigin(
  brandingData:
    | Pick<BrandingData, 'host' | 'subHost' | 'portalOrigin' | 'brandingZone'>
    | undefined
    | null,
): string {
  if (brandingData?.portalOrigin) return brandingData.portalOrigin;
  const host =
    brandingData?.host ||
    (brandingData?.subHost ? `${brandingData.subHost}.${resolveBrandingZone(brandingData)}` : '');
  if (!host) return '';
  return host.startsWith('localhost') ? `http://${host}` : `https://${host}`;
}

/**
 * Group Security §4 — "role caps the grant". A folder grant in `securityGroupIds[{groupId,isEdit}]`
 * only confers EDIT when the GROUP's role in that folder's teamspace allows edit; `isEdit` alone is
 * not enough (a Viewer-role group with `isEdit:true` is view-only).
 *
 * This is the wire shape of the backend principal cap — one entry per GROUP, mirroring
 * `me.groupCapabilities` / open-api `GroupCapability` (`shared/access-resolver.ts`
 * `GroupEditCapability` + `toGroupCapabilityList`), computed once at auth. Read it with
 * `isGroupEditCapable` (`apps/web/src/lib/folder-security.ts`) — never field-by-field at a call site,
 * or the three-way precedence below drifts from the backend.
 */
export interface GroupEditCapability {
  /** The user group this cap belongs to (matches `securityGroupIds[].groupId`). */
  groupId: string;
  /**
   * The answer for a teamspace the group holds NO role statement in — deny-by-default. `true` only
   * for a group that is edit-capable somewhere and holds no account-level statement denying it (the
   * migrated cross-team share case: the group's role lives in its own teamspace, not the folder's).
   */
  editCapableDefault: boolean;
  /** Teamspaces whose resolved group role ALLOWS edit. Checked FIRST. */
  editTeamIds: string[];
  /**
   * Teamspaces whose resolved group role DENIES edit — checked before `editCapableDefault`, so the
   * statement covering the folder's OWN teamspace wins over a role the group holds elsewhere.
   */
  viewOnlyTeamIds: string[];
}

export interface MeTeam {
  _id: string;
  name: string;
  role: RoleType;
  permissions?: Record<string, boolean>;
  isTeamBilling?: boolean;
  /** Picked teamspace icon (a `TEAMSPACE_ICONS` key); unset = the name's first letter. */
  icon?: string | null;
  /** Picked icon colour (#rrggbb). */
  iconColor?: string | null;
  /**
   * §12 (#2486): may this teamspace's non-admin members create or edit at the section Root?
   *
   * This IS the teamspace's own "Members can create or edit at the section root" toggle, and since
   * the §12 inversion it is the only thing that can take the capability away. It lives on the
   * teamspace because that is what it is a property of — how a member got in is irrelevant.
   *
   * `true` and UNSET both mean allowed; only an explicit `false` denies. Default/ROOT has no
   * teamspace document, so it is always unset here and therefore never denied.
   */
  canEditHomePath?: boolean | null;
}

export interface MeResponse {
  _id: string;
  accountId: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  username: string;
  language: string;
  rootRole: RoleType;
  teams: MeTeam[];
  feature: Record<string, boolean>;
  featureData: Record<string, unknown>;
  brandingData: BrandingData;
  planFeatures: PlanFeature[];
  payment: Record<string, unknown>;
  accountInfo: Record<string, unknown>;
  userSettings: Record<string, unknown>;
  isSuperUser: boolean;
  /**
   * True when `me` was resolved via the share-link augmented `_getAuthFromDB`
   * path on the backend (anonymous viewer with `shareLinkObjectId` claim, OR a
   * real signed-in user whose `req.user` was overwritten by
   * `_augmentWithShareLinkContext`). `accountId`/`teams` reflect the SHARE
   * OWNER's context — NOT the caller's real account. Frontend uses this flag
   * to detect cross-team / cross-company share access without comparing
   * accountId (which is masked by augmentation).
   */
  isGuestUser?: boolean;
  ownerEmail: string;
  ownerName: string;
  passwordChangeRequire: boolean;
  accountStatus: boolean;
  fileCdnkeyData?: string;
  appMetas?: AppMeta[];
  appMetaGroups?: MeAppMetaGroup[];
  scioVersion?: number;
  /** User Groups (Group Security v3) this user belongs to — empty for folder-security accounts. */
  groupIds?: string[];
  /**
   * Group Security §4 — the ROLE CAP for each of the user's groups, computed ONCE by the backend at
   * auth so no FE surface has to look a role up. ONE entry per GROUP, carrying its per-teamspace
   * verdicts (`editTeamIds` / `viewOnlyTeamIds`) plus the default for teamspaces it has no statement
   * in. Absent/empty ⇒ the cap is UNKNOWN ⇒ group grants confer VIEW only (fail closed). Read it with
   * `isGroupEditCapable` (`apps/web/src/lib/folder-security.ts`); it must be SELECTED in `ME_QUERY`
   * (`packages/graphql-client/src/operations/auth.ts`) or every group-granted folder renders
   * read-only.
   */
  groupCapabilities?: GroupEditCapability[];
  /**
   * Group Security v3 (§12, per-type) — the caller's effective home teamspace + PER-TYPE home folders,
   * resolved by the backend from the first of their group(s) (assignment order) that defines one. Drives
   * login auto-land: switch to `homeTeamId`, then land in the home folder matching the section's content
   * type. Empty/undefined = no home configured.
   */
  homeTeamId?: string;
  // Each home carries `teamId` (the folder's owning team — a path is not unique across teams); the FE
  // navigates into the home using this team, not the login `homeTeamId`.
  homeFolders?: Array<{ type: string; path: string; teamId?: string }>;
  /**
   * §12 ACCOUNT-WIDE (#2486): the ADMIN shortcut and nothing else — the server sets it for a super
   * user, a super role, or an account admin. The per-teamspace answer is `MeTeam.canEditHomePath`,
   * and `useRootActionLockValue` ORs the two exactly as the backend gate does.
   */
  canEditHomePath?: boolean;
  /**
   * Group Security v5 §4e — the CAPABILITY plane: every `<domain>.<verb>` permission key this user
   * holds, computed by the backend as the union of their role bundles, group-derived roles and
   * per-user `extraPermissions`. Read it with `usePermissions().can('audit.view')` — never by
   * indexing this array by hand, because `can()` also fails closed when the field is absent.
   *
   * Must be SELECTED in `ME_QUERY` (`packages/graphql-client/src/operations/auth.ts`) or `can()` sees
   * nothing and answers false for everything.
   *
   * This is CAPABILITY, never REACH (spec §3b): `assets.edit` means "may edit the assets he can
   * reach"; which folders those are stays `groupCapabilities` + the folder-security mirror.
   */
  effectivePermissions?: string[];
  /**
   * The capability plane at ROOT scope only — `rootRole`'s bundle plus the Default teamspace's own
   * entry (scio3#2351). `effectivePermissions` is the union across every teamspace the user
   * reaches, which is the wrong question for an ACCOUNT-WIDE surface. Read via `canAtRoot()`.
   */
  rootPermissions?: string[];
  inviteUser?: { email: string; fullname: string; role: string };
  /**
   * Referral mirror, populated by open-api from the referral engine's local
   * mirror (no engine round-trip). `referralLink` = vanityBaseUrl + '/' + code.
   * Read by the referral widget via `preloadedCode` to render the share link.
   */
  referralCode?: string;
  referralLink?: string;
  /**
   * Set when the user just hit an expansion moment (first paid / first
   * expansion). The referral widget opens the refer modal once, then calls
   * clearReferralPrompt. Null/undefined otherwise.
   */
  referralPromptPending?: string;
  /**
   * Per-user feature-discovery map keyed by feature slug (e.g. 'optidev').
   * `firstSeenAt` is an ISO date string (the first time this user saw the
   * highlighted tab); `clicks` counts how many times they've clicked it.
   * Drives the OptiDev tab highlight — see `useFeatureHighlight`.
   */
  featureDiscovery?: Record<string, { firstSeenAt: string; clicks: number }>;
}

export interface MeAppMetaGroup {
  _id: string;
  title: string;
  description?: string;
  icon?: string;
  appTypes: string[];
  order?: number;
  isShowWelcome?: boolean;
  orderWelcome?: number;
  isFree?: boolean;
  isSkipSelectApp?: boolean;
  settings?: {
    onlyShowDescription?: boolean;
    redirectLabel?: string;
    redirectURL?: string;
  };
  appContent?: string;
}

export interface PlanFeature {
  _id: string;
  name: string;
  features: { name: string; enable: boolean }[];
}

export interface UserSettings {
  _id: string;
  isShowWelcomeTips: boolean;
  isShowOrderAlertStick: boolean;
  onboardFormSkip: boolean;
  onboardFormSubmitted: boolean;
  firstGetStarted: boolean;
  firstGetStartedDate: string;
  themeMode: string;
  dateFormat: string;
  timeFormat: string;
  numberFormat: string;
  scaleTemperature: string;
  windSpeedUnit: string;
  defaultValueType: string;
  assetIsDupClickMode?: boolean;
  isHideBannerPromotionForPaidUser: boolean;
  // Analytics first-event flags (persisted via updateUserSettingsAuth)
  firstAssetCreated?: boolean | null;
  firstPlaylistCreated?: boolean | null;
  firstScheduleCreated?: boolean | null;
  firstTeamMemberInvited?: boolean | null;
  firstTemplateUsed?: boolean | null;
  firstDeviceOrdered?: boolean | null;
  needAnalyticFirst?: boolean;
}
